import { useRef, useState } from "react";
import DOMPurify from "dompurify";
import { setConsent } from "../api/webcheckinApi";
import { useWizardContext } from "./shared";
import { SignaturePad, type SignaturePadHandle } from "../components/SignaturePad";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function ConsentScreen({ token }: { token: string }) {
  const { state, dispatch } = useWizardContext();
  const { settings } = state.payload!;

  const [tnc, setTnc] = useState(false);
  const [house, setHouse] = useState(false);
  const [gdpr, setGdpr] = useState(false);
  const [mkt, setMkt] = useState(false);
  // «No emails after my stay» — offered where the guest confirms the address
  // (Law 3471/2006 art. 11 §3). Recorded with the Guest Experience service.
  const [noPostStay, setNoPostStay] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const padRef = useRef<SignaturePadHandle | null>(null);

  const canContinue = tnc && house && gdpr && hasSignature && !busy;

  async function handleContinue() {
    if (!padRef.current) return;
    setBusy(true); setError(null);
    try {
      const dataUrl = padRef.current.toDataURL();
      const base64 = dataUrl.split(",")[1] ?? "";
      await setConsent(token, {
        tncAccepted: true,
        houseRulesAccepted: true,
        gdprAccepted: true,
        marketingOptIn: mkt,
        emailOptOut: noPostStay,
        signaturePngBase64: base64,
      });
      dispatch({ type: "next" });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const houseRulesHtml = settings.houseRulesHtml
    ? DOMPurify.sanitize(settings.houseRulesHtml)
    : null;

  return (
    <div>
      <h1 className="screen-h1">Consent and signature</h1>
      <p className="screen-p mt-2">Please accept the terms below and sign to finalise your check-in.</p>

      {houseRulesHtml && (
        <div
          className="prose prose-sm mt-4 max-h-40 overflow-y-auto rounded-lg border bg-muted/40 p-3 text-sm text-slate-700"
          dangerouslySetInnerHTML={{ __html: houseRulesHtml }}
        />
      )}

      <SectionHeading required>Required to continue</SectionHeading>
      <div className="space-y-3 rounded-lg border bg-card p-4 shadow-sm">
        <ConsentRow
          id="c-house"
          checked={house}
          onChange={setHouse}
          label="I have read and accept the property's house rules."
        />
        <Divider />
        <ConsentRow
          id="c-tnc"
          checked={tnc}
          onChange={setTnc}
          label={
            <>
              I accept the{" "}
              {settings.tncUrl ? (
                <a className="text-primary underline" href={settings.tncUrl} target="_blank" rel="noreferrer">terms &amp; conditions</a>
              ) : (
                "terms & conditions"
              )}
              .
            </>
          }
        />
        <Divider />
        <ConsentRow
          id="c-gdpr"
          checked={gdpr}
          onChange={setGdpr}
          label={
            <>
              I consent to the processing of my personal data as described in the{" "}
              {settings.gdprPrivacyUrl ? (
                <a className="text-primary underline" href={settings.gdprPrivacyUrl} target="_blank" rel="noreferrer">privacy policy</a>
              ) : (
                "privacy policy"
              )}
              .
            </>
          }
        />
      </div>

      <SectionHeading>Optional</SectionHeading>
      <div className="space-y-3 rounded-lg border bg-card p-4 shadow-sm">
        <ConsentRow
          id="c-mkt"
          checked={mkt}
          onChange={setMkt}
          label={`I'd like to receive offers and news from ${state.payload!.property.name}.`}
        />
        <Divider />
        <ConsentRow
          id="c-no-post-stay"
          checked={noPostStay}
          onChange={setNoPostStay}
          label="Don't send me emails after my stay (thank-you notes, review requests). Booking and check-in emails still arrive."
        />
      </div>

      <SectionHeading required>Signature</SectionHeading>
      <SignaturePad controlRef={padRef} onChange={setHasSignature} />
      <div className="mt-1 flex justify-end">
        <Button variant="link" size="sm" type="button" onClick={() => padRef.current?.clear()} className="h-auto p-0 text-xs text-muted-foreground">
          Clear signature
        </Button>
      </div>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <Button className="mt-6 w-full h-11" disabled={!canContinue} onClick={handleContinue}>
        {busy ? "Saving…" : "Continue"}
      </Button>
      {!canContinue && !busy && (
        <p className="mt-3 text-xs text-amber-700">
          To continue, tick the three required boxes and sign in the box above.
        </p>
      )}
    </div>
  );
}

function SectionHeading({ required, children }: { required?: boolean; children: React.ReactNode }) {
  return (
    <div className="mt-5 mb-2 flex items-center gap-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</h2>
      <span
        className={cn(
          "inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ring-1",
          required
            ? "bg-destructive/10 text-destructive ring-destructive/30"
            : "bg-muted text-muted-foreground ring-border"
        )}
      >
        {required ? "Required" : "Optional"}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="border-t" />;
}

function ConsentRow({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 py-1">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
        className="mt-0.5"
      />
      <label htmlFor={id} className="text-sm leading-relaxed text-slate-700 cursor-pointer">
        {label}
      </label>
    </div>
  );
}
